import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Armchair, ChevronDown, Clock3, MapPin, MonitorPlay, ShieldCheck, TrendingUp, Trophy, Users } from "lucide-react";
import { API } from "@/lib/api";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import LocationCard from "@/components/LocationCard";
import TipwinLogo from "@/components/TipwinLogo";
import HeroSlider from "@/components/HeroSlider";

const HERO_SLIDES = [
  { id: "filiale", image: "/media/ahlen-innenraum.webp", tag: "Unsere Filiale", alt: "Innenraum der Wettvermittlungsstelle: Theke mit Quoten-Monitoren, Tische mit Stühlen und Wettterminals" },
  { id: "quoten", image: "/media/quoten-wall.webp", tag: "Live-Quoten", alt: "Monitor-Wall mit Live-Wettquoten der Bundesliga und internationaler Ligen" },
  { id: "terminal", image: "/media/terminal-closeup.webp", tag: "Terminal-Technik", alt: "Modernes Wettterminal mit Quoten-Anzeige im Lounge-Ambiente" },
  { id: "annahme", image: "/media/rietberg-innenraum.webp", tag: "Wettannahme", alt: "Wettannahmestelle mit Terminals neben dem Eingang und Theke mit Quoten-Monitor" },
  { id: "wm2026", image: "/media/stadium-wm.webp", tag: "Große Events", alt: "Fußball-Stadion unter Flutlicht – WM, EM und Champions League live bei uns erleben" },
];

const EXPERIENCE = [
  {
    icon: MonitorPlay,
    title: "Live-Sport",
    text: "Fußball, Bundesliga und internationale Top-Spiele – auf großen Bildschirmen, mitten im Geschehen.",
    image: "/media/stadium-fans.webp",
    alt: "Stadion-Atmosphäre bei einem Fußball-Abendspiel unter Flutlicht",
  },
  {
    icon: Users,
    title: "Persönlicher Service",
    text: "Ein Team vor Ort, das dich kennt. Beratung, Abgabe und Auszahlung – persönlich und unkompliziert.",
    image: "/media/rietberg-innenraum.webp",
    alt: "Theke mit Quoten-Monitor und Wettterminals in der Wettannahmestelle (KI-Illustration)",
  },
  {
    icon: Clock3,
    title: "Lounge-Atmosphäre",
    text: "Kein anonymes Wettbüro: hochwertige Sitzbereiche, ruhiges Ambiente, Platz zum Verweilen.",
    image: "/media/ahlen-innenraum.webp",
    alt: "Sitzbereich mit Tischen und Stühlen vor Quoten-Monitoren in der Filiale (KI-Illustration)",
  },
];

const OFFERS = [
  {
    icon: MonitorPlay,
    title: "Live-Sport auf Großleinwänden",
    text: "Bundesliga, Champions League, EM und WM: Wir zeigen die großen Spiele live in HD – in Ahlen sogar auf zwei 85-Zoll-Bildschirmen.",
    testId: "home-offer-card-livesport",
  },
  {
    icon: Trophy,
    title: "Über 30 verschiedene Sportarten",
    text: "Ein breites Spektrum an Sportwetten: Fußball, Tennis, Basketball, Handball und vieles mehr – vom Top-Spiel bis zur Randsportart.",
    testId: "home-offer-card-sportarten",
  },
  {
    icon: TrendingUp,
    title: "Live-Quoten & Ergebnisse in Echtzeit",
    text: "Aktuelle Quoten und Live-Ergebnisse auf zahlreichen Monitoren in der Filiale – du bleibst immer auf dem neuesten Stand.",
    testId: "home-offer-card-quoten",
  },
  {
    icon: Armchair,
    title: "Viele Sitzplätze & Lounge-Atmosphäre",
    text: "Bequeme Tische und Stühle mitten im Geschehen: Verfolge die Spiele in entspannter Runde – mit Freunden oder anderen Fans.",
    testId: "home-offer-card-lounge",
  },
  {
    icon: Users,
    title: "Persönliche Beratung an der Theke",
    text: "Unser geschultes Team nimmt sich Zeit: Wettschein abgeben, Fragen klären oder Tipps zum Angebot – direkt und persönlich.",
    testId: "home-offer-card-service",
  },
  {
    icon: ShieldCheck,
    title: "Sicher & geprüft",
    text: "Behördlich zugelassene Wettvermittlungsstellen mit geprüftem Spielerschutz – Teilnahme selbstverständlich erst ab 18 Jahren.",
    testId: "home-offer-card-sicher",
  },
];

const FAQS = [
  {
    q: "Wo finde ich das nächste Tipwin Wettbüro in meiner Nähe?",
    a: "Unsere Wettvermittlungsstellen findest du in Ahlen (Bahnhofstraße 69) und Rietberg (Westerwieher Straße 1) – gut erreichbar aus dem ganzen Umkreis, z. B. Beckum, Ennigerloh, Sendenhorst, Gütersloh, Verl oder Rheda-Wiedenbrück. Nutze einfach unseren Standort-Finder mit PLZ-Suche.",
  },
  {
    q: "Was ist der Unterschied zwischen Wettbüro, Wettannahmestelle und Wettvermittlungsstelle?",
    a: "Im Alltag meinen alle drei dasselbe: einen Ort, an dem du deine Sportwetten persönlich abgibst. Wir sind eine behördlich zugelassene Wettvermittlungsstelle der Marke Tipwin – du gibst deinen Schein bei uns ab, wir vermitteln ihn an den lizenzierten Veranstalter.",
  },
  {
    q: "Kann ich bei euch Live-Wetten platzieren?",
    a: "Ja – bei uns tippst du live, während das Spiel läuft. Quoten und Ergebnisse laufen in Echtzeit auf unseren Monitoren, dazu zeigen wir die Spiele auf Großleinwänden (in Ahlen auf zwei 85-Zoll-Bildschirmen).",
  },
  {
    q: "Auf welche Sportarten kann ich wetten?",
    a: "Über 30 Sportarten: Fußball von der Bundesliga über die Champions League bis zu Weltmeisterschaft und Europameisterschaft – dazu Tennis, Basketball, Handball und viele weitere, vom Top-Spiel bis zur Randsportart.",
  },
  {
    q: "Ab wie vielen Jahren darf ich bei euch wetten?",
    a: "Sportwetten sind ab 18 Jahren – das prüfen wir streng, bitte bring einen gültigen Ausweis mit. Mehr dazu findest du auf unserer Spielerschutz-Seite.",
  },
  {
    q: "Wie bekomme ich meinen Gewinn ausgezahlt?",
    a: "Direkt an unserer Theke – schnell, persönlich und unkompliziert, ohne Warten auf Überweisungen.",
  },
  {
    q: "Warum ins Wettbüro statt online zu wetten?",
    a: "Weil es bei uns ein Erlebnis ist: Spiele live auf der Großleinwand, Lounge-Atmosphäre, persönliche Beratung und sofortige Auszahlung – alles behördlich zugelassen und OASIS-geprüft.",
  },
];

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Sportwetten NRW GmbH",
  url: typeof window !== "undefined" ? window.location.origin : "",
  description: "Lokale Tipwin-Wettvermittlungsstellen in Nordrhein-Westfalen mit Live-Sport und persönlichem Service.",
};

function FaqItem({ question, answer, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-[#E4E4E7] bg-white transition-colors duration-300 hover:border-[#E11D2A]/40" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left sm:py-3.5"
        data-testid={`${testId}-toggle`}
      >
        <span className="text-sm font-semibold text-[#1A1A1A] sm:text-base">{question}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#E11D2A] transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="px-4 pb-4 text-sm leading-relaxed text-[#52525B]">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [locations, setLocations] = useState(null);
  const [promo, setPromo] = useState(null);
  const locationsGridRef = useRef(null);
  const experienceGridRef = useRef(null);
  const offerGridRef = useRef(null);

  useEffect(() => {
    // TikTok-Paging (scroll-snap) nur auf der Startseite aktivieren;
    // Scroll-Container (#root) beim Betreten auf Seitenanfang setzen
    document.documentElement.classList.add("snap-paging");
    const root = document.getElementById("root");
    if (root) root.scrollTop = 0;
    return () => document.documentElement.classList.remove("snap-paging");
  }, []);

  // Mobil: Hoehe der ersten Karte exakt auf den Seiten-Rest setzen, damit
  // Sektionskopf + erste Karte ohne Leerraum/Anschnitt genau einen Screen fuellen.
  useEffect(() => {
    const grids = [locationsGridRef.current, experienceGridRef.current, offerGridRef.current].filter(Boolean);
    if (!grids.length) return undefined;
    const mq = window.matchMedia("(max-width: 767.98px)");
    const NAVBAR_PX = 66; // 4.125rem (Navbar + rote Linie)
    const SAFETY_PX = 2; // Borders/Rundung
    // Gestenleiste/Home-Balken (safe-area) einmalig ausmessen
    const probe = document.createElement("div");
    probe.style.cssText = "position:absolute;visibility:hidden;padding-bottom:env(safe-area-inset-bottom,0px)";
    document.body.appendChild(probe);
    const SAFE_BOTTOM_PX = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    probe.remove();
    const BOTTOM_INSET_PX = 12 + SAFE_BOTTOM_PX; // padding-bottom .snap-pages-inner
    const measure = () => {
      if (!mq.matches) return;
      grids.forEach((grid) => {
        const inner = grid.closest(".snap-pages-inner");
        if (!inner) return;
        const lead = grid.getBoundingClientRect().top - inner.getBoundingClientRect().top;
        const h = Math.floor(window.innerHeight - NAVBAR_PX - lead - BOTTOM_INSET_PX - SAFETY_PX);
        grid.style.setProperty("--snap-card1-h", `${Math.max(h, 160)}px`);
      });
    };
    let lastWidth = window.innerWidth;
    const onResize = () => {
      // Reine Hoehen-Aenderung (z. B. Browser-Leiste auf exotischen Browsern)
      // ignorieren — kein Re-Layout mitten im Scrollen
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      measure();
    };
    measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", measure);
    };
  }, [locations]);

  // Footer-Zone: EIN Snap-Stopp am Anfang des schwarzen Footers (Standorte/
  // Rechtliches sichtbar), danach komplett freies Scrollen bis ganz nach unten
  // ("normaler Modus", auch Wisch-Fling). Oberhalb des Footers Snap wieder aktiv.
  // Positionsbasiert (ohne Cache), damit Layout-Verschiebungen nach dem Laden egal sind.
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return undefined;
    const mq = window.matchMedia("(max-width: 767.98px)");
    const onScroll = () => {
      if (!mq.matches) return;
      const footer = document.querySelector('[data-testid="site-footer"]');
      if (!footer) return;
      // 66 = Footer exakt eingerastet (unter Navbar); <= 68: Footer-Zone = frei
      root.classList.toggle("snap-free", footer.getBoundingClientRect().top <= 68);
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      root.classList.remove("snap-free");
    };
  }, []);

  useEffect(() => {
    let locationsOk = true;
    const locationsReq = API.get("/locations")
      .then((res) => setLocations(res.data))
      .catch(() => {
        setLocations([]);
        locationsOk = false;
      });
    const promoReq = API.get("/promotions", { params: { category: "wm2026" } })
      .then((res) => setPromo(res.data[0] || null))
      .catch(() => setPromo(null));
    Promise.allSettled([locationsReq, promoReq]).finally(() => {
      if (locationsOk) window.__swnPageReady = true;
      else window.__swnPageError = true;
    });
  }, []);

  return (
    <>
      <Seo
        title="Sportwetten NRW × Tipwin | Wettbüros in deiner Nähe"
        description="Lokale Tipwin-Wettvermittlungsstellen der Sportwetten NRW GmbH: Live-Sport, persönlicher Service und Lounge-Atmosphäre in Rietberg und Ahlen. Standorte, Öffnungszeiten und Anfahrt."
        path="/"
        jsonLd={ORG_JSONLD}
      />

      <section className="relative flex min-h-[calc(100dvh-4.125rem)] flex-col overflow-hidden" data-testid="hero-section" data-snap="start">
        <HeroSlider
          slides={HERO_SLIDES}
          bottomBar={
            <>
              <span className="inline-flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#E11D2A] font-display text-[10px] font-extrabold text-[#F54855]">18+</span>
                Geprüfter Spielerschutz
              </span>
              <span className="inline-flex items-center gap-2">
                <MonitorPlay className="h-4 w-4 text-[#F54855]" aria-hidden="true" /> Live-Sport auf Großbildschirmen
              </span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#F54855]" aria-hidden="true" /> {locations?.length ?? 2} Standorte in NRW
              </span>
              <span className="hidden h-4 w-px bg-[#F5F5F3]/25 lg:inline-block" aria-hidden="true" />
              <a
                href="https://www.buwei.de/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-4 transition-colors hover:text-white hover:underline"
                data-testid="hero-link-buwei"
              >
                Informationen und Hilfe unter www.buwei.de
              </a>
              <span className="font-semibold text-[#F5F5F3]">Veranstalter: Tipwin Ltd. (GGL White List)</span>
            </>
          }
        />
        <div className="relative mx-auto flex w-full max-w-7xl flex-1 items-start px-4 pb-48 pt-6 sm:items-center sm:px-6 sm:py-28 lg:px-12">
          <div>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-[#F54855]">
              Sportwetten NRW GmbH <span aria-hidden="true">×</span> <TipwinLogo className="h-6 w-auto" />
            </p>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#F5F5F3] sm:mt-6 sm:text-6xl lg:text-7xl">
              Live-Sport. Echte Atmosphäre. <span className="text-[#F54855]">Dein Standort vor Ort.</span>
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#F5F5F3]/80 sm:mt-6 sm:text-base lg:text-lg">
              Unsere Tipwin-Wettvermittlungsstellen in NRW verbinden modernste Wettterminal-Technik
              mit dem Charakter einer Premium Sports Lounge – persönlich, sicher und direkt in deiner Nähe.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:mt-8 sm:flex-row">
              <Link
                to="/standorte"
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-[#E11D2A] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#F02837]"
                data-testid="hero-cta-standort-finden"
              >
                Standort finden <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href="#erlebnis"
                className="inline-flex min-h-[48px] items-center justify-center rounded-lg border border-[#F5F5F3]/25 bg-[#0E0E10]/30 px-6 py-3 text-base font-semibold text-[#F5F5F3] backdrop-blur transition-colors hover:border-[#F5F5F3]/50 hover:bg-[#0E0E10]/50"
                data-testid="hero-cta-erlebnis"
              >
                Das Erlebnis entdecken
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA]" data-testid="home-locations-section" data-snap="start">
        <div className="snap-pages-inner mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <Reveal>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E11D2A]">Vor Ort</p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-[#1A1A1A] sm:text-3xl lg:text-4xl">
                Unsere Standorte
              </h2>
            </div>
            <Link to="/standorte" className="inline-flex items-center gap-2 text-sm font-semibold text-[#E11D2A] transition-colors hover:text-[#F02837]" data-testid="home-link-alle-standorte">
              Alle Standorte & Finder <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
        <div ref={locationsGridRef} className="snap-pages-grid mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          {locations === null ? (
            [0, 1].map((i) => (
              <div key={i} className="overflow-hidden rounded-xl border border-[#E4E4E7] bg-white shadow-sm" aria-hidden="true" data-testid="home-locations-skeleton">
                <div className="aspect-[16/9] bg-[#F4F4F5]" />
                <div className="space-y-3 p-6">
                  <div className="h-5 w-2/3 rounded bg-[#F4F4F5]" />
                  <div className="h-4 w-1/3 rounded bg-[#F4F4F5]" />
                  <div className="h-4 w-full rounded bg-[#F4F4F5]" />
                  <div className="h-11 w-40 rounded-lg bg-[#F4F4F5]" />
                </div>
              </div>
            ))
          ) : (
            locations.map((loc, i) => (
              <Reveal key={loc.slug} delay={i * 0.1} data-snap={i === 0 ? undefined : "start"}>
                <LocationCard location={loc} tone="bright" />
              </Reveal>
            ))
          )}
        </div>
        </div>
      </section>

      <section id="erlebnis" className="border-y border-[#27272A] bg-[#111114]" data-testid="home-experience-section" data-snap="start">
        <div className="snap-pages-inner mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#F54855]">Das Erlebnis</p>
            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-[#F5F5F3] sm:text-3xl lg:text-4xl">
              Mehr Sports Lounge als Wettbüro
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#A1A1AA]">
              Kein Neon, kein Trubel – sondern hochwertiges Ambiente, aufmerksamer Service und
              modernste Tipwin-Technik. So fühlt sich Sportwetten vor Ort heute an.
            </p>
          </Reveal>
          <div ref={experienceGridRef} className="snap-pages-grid mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {EXPERIENCE.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1} data-snap={i === 0 ? undefined : "start"}>
                <article className="group overflow-hidden rounded-xl border border-[#27272A] bg-[#16161A] transition-colors duration-300 hover:border-[#E11D2A]/40">
                  <div className="snap-grow aspect-[16/10] overflow-hidden bg-[#1E1E24]">
                    <img
                      src={item.image}
                      alt={item.alt}
                      width="800"
                      height="500"
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="snap-card-body p-6">
                    <item.icon className="h-6 w-6 text-[#F54855]" aria-hidden="true" />
                    <h3 className="mt-4 font-display text-xl font-semibold text-[#F5F5F3]">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">{item.text}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="wm-2026" className="relative overflow-hidden" data-testid="home-wm2026-section" data-snap="start">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src={promo?.image_url || "/media/stadium-wm.webp"}
            alt=""
            loading="lazy"
            width="1920"
            height="1080"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E0E10]/70 via-[#0E0E10]/30 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#F54855]">Event</p>
            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-[#F5F5F3] sm:text-3xl lg:text-4xl">
              {promo?.title || "WM, EM & Champions League live bei uns erleben"}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#F5F5F3]">
              {promo?.body ||
                "Großer Fußball verdient große Bilder: Bundesliga, Champions League, Europa League und DFB-Pokal genauso live wie Weltmeisterschaft und Europameisterschaft – jedes Top-Spiel auf der Großleinwand, dazu Live-Wetten in Echtzeit. Live Fußball schauen und mittippen: Genau dafür sind unsere Sports Lounges da."}
            </p>
            <Link
              to={promo?.cta_url || "/standorte"}
              className="mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-lg bg-[#E11D2A] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#F02837]"
              data-testid="wm2026-cta"
            >
              {promo?.cta_label || "Standort finden"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section id="angebot" className="border-t border-[#E4E4E7] bg-[#FAFAFA]" data-testid="home-offer-section" data-snap="start">
        <div className="snap-pages-inner mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E11D2A]">Unser Angebot</p>
            <h2 className="mt-3 max-w-3xl font-display text-2xl font-bold tracking-tight text-[#1A1A1A] sm:text-3xl lg:text-4xl">
              Alles rund um Sportwetten – live, groß und persönlich
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#52525B] sm:text-base">
              Von der Bundesliga bis zur Weltmeisterschaft: Bei uns verfolgst du jedes Top-Spiel in
              HD auf großen Bildschirmen – und tippst mit, während es läuft.
            </p>
          </Reveal>
          <div ref={offerGridRef} className="snap-pages-grid mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[OFFERS.slice(0, 2), OFFERS.slice(2, 4), OFFERS.slice(4, 6)].map((pageItems, pageIdx) => (
              <div key={pageIdx} className="offer-page contents" data-snap={pageIdx === 0 ? undefined : "start"}>
                {pageItems.map((item, i) => (
                  <Reveal key={item.testId} delay={i * 0.05}>
                    <div className="h-full rounded-xl border border-[#E4E4E7] bg-white p-4 shadow-sm transition-colors duration-300 hover:border-[#E11D2A] sm:p-6" data-testid={item.testId}>
                      <item.icon className="h-7 w-7 text-[#E11D2A]" aria-hidden="true" />
                      <h3 className="mt-4 font-display text-lg font-semibold text-[#1A1A1A]">{item.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-[#52525B]">{item.text}</p>
                    </div>
                  </Reveal>
                ))}
                {pageIdx === 2 && (
                  <Reveal delay={0.15} className="offer-cta sm:col-span-2 lg:col-span-3">
                    <div className="flex flex-col gap-3 sm:mt-4 sm:flex-row">
                      <Link
                        to="/standorte"
                        className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-[#E11D2A] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#F02837]"
                        data-testid="home-offer-cta"
                      >
                        Jetzt Filiale entdecken <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </div>
                  </Reveal>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#E4E4E7] bg-white" data-testid="home-company-section" data-snap="start">
        <div className="snap-pages-inner mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="snap-fill grid grid-cols-1 items-start gap-10 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E11D2A]">Unternehmen</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-[#1A1A1A] sm:text-3xl lg:text-4xl">
              Sportwetten NRW GmbH – lokal verwurzelt, stark vernetzt
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-[#52525B] sm:text-base">
              Die Sportwetten NRW GmbH mit Sitz in Rietberg betreibt lokale Wettvermittlungsstellen
              in Nordrhein-Westfalen. In unseren Filialen vermitteln wir Sportwetten der Marke{" "}
              <span className="font-semibold text-[#1A1A1A]">Tipwin</span> – mit klarem Fokus auf
              persönlichen Service, Transparenz und ein hochwertiges Besuchserlebnis. Besonders viel
              Wert legen wir dabei auf eine <strong className="text-[#1A1A1A]">schnelle Auszahlung</strong> deiner Gewinne.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-xl border border-[#E4E4E7] bg-[#F4F4F5] p-4 sm:p-8" data-testid="home-responsibility-card">
              <ShieldCheck className="h-8 w-8 text-[#E11D2A]" aria-hidden="true" />
              <h3 className="mt-4 font-display text-xl font-semibold text-[#1A1A1A]">Verantwortung zuerst</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#52525B]">
                Glücksspiel ist kein Spiel für Minderjährige – und kein Weg, Geldprobleme zu lösen.
                Teilnahme ab 18 Jahren. Wir informieren transparent über Risiken und verweisen auf
                unabhängige, kostenlose Hilfsangebote.
              </p>
              <Link
                to="/spielerschutz"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#E11D2A] transition-colors hover:text-[#F02837]"
                data-testid="home-link-spielerschutz"
              >
                Mehr zum Spielerschutz <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
        </div>
      </section>

      <section id="faq" className="border-t border-[#E4E4E7] bg-[#FAFAFA]" data-testid="home-faq-section" data-snap="start">
        <div className="snap-pages-inner mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E11D2A]">Gut zu wissen</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-[#1A1A1A] sm:text-3xl lg:text-4xl">
              Häufige Fragen
            </h2>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 gap-2 sm:mt-8 sm:gap-3 lg:grid-cols-2">
            {FAQS.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.05}>
                <FaqItem question={item.q} answer={item.a} testId={`home-faq-item-${i + 1}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
